import { difference, intersection, keyBy } from '@exodus/basic-utils';
import { CUSTODIAL_SOURCES, SOFTWARE_SEED_SOURCES, } from '@exodus/models/lib/wallet-account/index.js';
const getName = (walletAccount) => walletAccount.toString();
export const getChanges = ({ walletAccountInstancesBefore, walletAccountInstancesAfter, supportedSources, }) => {
    const filterSupportedWallets = (w) => supportedSources.has(w.source);
    walletAccountInstancesBefore = walletAccountInstancesBefore.filter(filterSupportedWallets);
    walletAccountInstancesAfter = walletAccountInstancesAfter.filter(filterSupportedWallets);
    const byNameBefore = keyBy(walletAccountInstancesBefore, getName);
    const byNameAfter = keyBy(walletAccountInstancesAfter, getName);
    const namesBefore = walletAccountInstancesBefore.map(getName);
    const namesAfter = walletAccountInstancesAfter.map(getName);
    const namesEnabledBefore = walletAccountInstancesBefore.filter((w) => w.enabled).map(getName);
    const namesEnabledAfter = walletAccountInstancesAfter.filter((w) => w.enabled).map(getName);
    const hardwareNamesBefore = walletAccountInstancesBefore.filter((w) => w.isHardware).map(getName);
    const hardwareNamesAfter = walletAccountInstancesAfter.filter((w) => w.isHardware).map(getName);
    const removedHardwareWalletAccounts = difference(hardwareNamesBefore, hardwareNamesAfter);
    return {
        disabled: difference(difference(namesEnabledBefore, namesEnabledAfter), removedHardwareWalletAccounts).filter((name) => namesAfter.includes(name)),
        added: difference(namesAfter, namesBefore),
        updated: intersection(namesBefore, namesAfter).filter((name) => !byNameBefore[name].equals(byNameAfter[name])),
        removedHardwareWalletAccounts,
    };
};
export const createEmptyAccounts = () => Object.create(null);
export const shouldDeriveIndex = (walletAccountData) => typeof walletAccountData.index !== 'number' &&
    !CUSTODIAL_SOURCES.includes(walletAccountData.source);
export const getNextIndex = ({ walletAccounts, seedId, source, compatibilityMode, fillIndexGapsOnCreation, }) => {
    const existing = Object.values(walletAccounts)
        .filter((w) => w.source === source &&
        w.compatibilityMode === compatibilityMode &&
        w.seedId === seedId &&
        !w.id &&
        (!fillIndexGapsOnCreation || w.enabled))
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    if (fillIndexGapsOnCreation) {
        return ([...existing.keys()].find((i) => existing[Number(i)]?.index !== Number(i)) ?? existing.length);
    }
    const indexes = existing
        .map(({ index }) => index)
        .filter((i) => typeof i === 'number');
    return Math.max(-1, ...indexes) + 1;
};
export const containWalletAccounts = (walletAccounts, otherWalletAccounts) => {
    return Object.entries(otherWalletAccounts).every(([key, otherWalletAccount]) => {
        const walletAccount = walletAccounts[key];
        return walletAccount && otherWalletAccount.equals(walletAccount);
    });
};
export const equalWalletAccounts = (walletAccounts, otherWalletAccounts) => {
    if (Object.keys(walletAccounts).length === Object.keys(otherWalletAccounts).length) {
        return containWalletAccounts(walletAccounts, otherWalletAccounts);
    }
    return false;
};
export const getUniqueTagForWalletAccount = ({ source, id, seedId, index }) => {
    const isSoftwareSeed = !!source && SOFTWARE_SEED_SOURCES.includes(source);
    const normalizedSource = isSoftwareSeed ? '' : (source ?? '');
    return ['source', normalizedSource, 'id', id, 'seedId', seedId, 'index', index].join('_');
};
